import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  withStyles,
} from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import styles from './styles';
import {ConfigureOptimizerAlgorithmDialog, InfoTooltip, TableSettings} from 'components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import TemplateStopSelectionTable from '../Tables/TemplateStopSelectionTable';
import { displayModes } from 'services/enums/displayModes';
import { TemplateStopType } from 'services/enums/TemplateStopType';
import TemplateStopIconLegend from 'components/Maps/MapComponents/TemplateStopIconLegend';
import useTableConfig from 'hooks/useTableConfig';
import DefaultConfig from '../Tables/config';
import { getTableKey } from 'services/util/tableConfigHelper';
import { TableKind } from 'services/enums/TableKind';
import Columns from '../Tables/columns';
import BaseTemplateStopSelectionMap from 'components/Maps/BaseMaps/BaseTemplateStopSelectionMap';
import {Settings as SettingsIcon, CheckCircleOutline, RemoveCircleOutline} from '@material-ui/icons';

function TemplateStopSelection(props) {
  const {
    classes,
    className,
    templates,
    templateStops,
    displayMode,
    t,
    selectedAssignedTemplateStops,
    selectedUnassignedTemplateStops,
    setSelectedAssignedTemplateStops,
    setSelectedUnassignedTemplateStops,
    configureOptimizerAlgorithmDialogState,
    setConfigureOptimizerAlgorithmDialogState,
    optimizerAlgorithmConfiguration,
    onConfigureOptimizerAlgorithmCancel,
    onConfigureOptimizerAlgorithmSave,
  } = props;
  const rootClassName = classNames(classes.formControl, className);
  const [unassignedTemplateStops, setUnassignedTemplateStops] = useState([]);
  const [templateStopConnectionSelection, setTemplateStopConnectionSelection] =
    useState(true);

  const { config, columnConfigs, updateConfig } = useTableConfig(
    DefaultConfig,
    displayMode,
    getTableKey(TableKind.TemplateStopSelectionTable, displayMode)
  );

  useEffect(() => {
    const templateStopsOnTemplatesIds = templates
      .map((t) => t.stops)
      .flat()
      .map((s) => s.id);

    const templateStopsNotOnTemplates = templateStops.filter(
      (templateStop) => !templateStopsOnTemplatesIds.includes(templateStop.id)
    );
    setUnassignedTemplateStops(templateStopsNotOnTemplates);
  }, [templateStops, templates]);

  const selectUnassignedTemplateStops = ({
    templateStops,
    unselect = false,
  }) => {
    let newSelectedUnassignedTemplateStops = JSON.parse(
      JSON.stringify(selectedUnassignedTemplateStops)
    );
    const newSelectedAssignedTemplateStops = JSON.parse(
      JSON.stringify(selectedAssignedTemplateStops)
    );
    templateStops.forEach((templateStop) => {
      if (unselect) {
        const index = newSelectedUnassignedTemplateStops.findIndex(
          (presentTemplateStop) => presentTemplateStop.id === templateStop.id
        );
        if (index >= 0) {
          newSelectedUnassignedTemplateStops.splice(index, 1);
        }
      } else {
        if (templateStopConnectionSelection) {
          const relatedTemplateStops = getRelatedTemplateStops(templateStop);
          addRelatedTemplateStops(
            relatedTemplateStops,
            newSelectedAssignedTemplateStops,
            newSelectedUnassignedTemplateStops
          );
        } else {
          newSelectedUnassignedTemplateStops.push(templateStop);
        }
      }
    });
    setSelectedUnassignedTemplateStops(newSelectedUnassignedTemplateStops);
    setSelectedAssignedTemplateStops(newSelectedAssignedTemplateStops);
  };

  const selectAssignedTemplateStops = ({ templateStops, unselect = false }) => {
    let newSelectedAssignedTemplateStops = JSON.parse(
      JSON.stringify(selectedAssignedTemplateStops)
    );
    const newSelectedUnassignedTemplateStops = JSON.parse(
      JSON.stringify(selectedUnassignedTemplateStops)
    );
    templateStops.forEach((templateStop) => {
      if (unselect) {
        const index = newSelectedAssignedTemplateStops.findIndex(
          (presentTemplateStop) => presentTemplateStop.id === templateStop.id
        );
        if (index >= 0) {
          newSelectedAssignedTemplateStops.splice(index, 1);
        }
      } else {
        if (templateStopConnectionSelection) {
          const relatedTemplateStops = getRelatedTemplateStops(templateStop);
          addRelatedTemplateStops(
            relatedTemplateStops,
            newSelectedAssignedTemplateStops,
            newSelectedUnassignedTemplateStops
          );
        } else {
          newSelectedAssignedTemplateStops.push(templateStop);
        }
      }
    });
    setSelectedAssignedTemplateStops(newSelectedAssignedTemplateStops);
    setSelectedUnassignedTemplateStops(newSelectedUnassignedTemplateStops);
  };

  function addRelatedTemplateStops(
    relatedTemplateStops,
    newSelectedAssignedTemplateStops,
    newSelectedUnassignedTemplateStops
  ) {
    relatedTemplateStops.forEach((templateStop) => {
      if (
        unassignedTemplateStops.findIndex(
          (unassignedTemplateStop) =>
            unassignedTemplateStop.id === templateStop.id
        ) >= 0
      ) {
        if (
          newSelectedUnassignedTemplateStops.findIndex(
            (unassignedTemplateStop) =>
              unassignedTemplateStop.id === templateStop.id
          ) < 0
        ) {
          newSelectedUnassignedTemplateStops.push(templateStop);
        }
      } else {
        if (
          newSelectedAssignedTemplateStops.findIndex(
            (assignedTemplateStop) =>
              assignedTemplateStop.id === templateStop.id
          ) < 0
        ) {
          newSelectedAssignedTemplateStops.push(templateStop);
        }
      }
    });
  }

  function selectAllTemplateStops() {
    const assignedTemplateStops = templates
      .map((template) => template.stops)
      .flat();
    setSelectedAssignedTemplateStops(assignedTemplateStops);
    setSelectedUnassignedTemplateStops(unassignedTemplateStops);
  }

  function deselectAllTemplateStops() {
    setSelectedAssignedTemplateStops([]);
    setSelectedUnassignedTemplateStops([]);
  }

  function getRelatedTemplateStops(templateStop) {
    const relatedTemplateStopIds = new Set();
    relatedTemplateStopIds.add(templateStop.id);

    if (templateStop.stopType === TemplateStopType.Pickup) {
      // for a pickup stop get all related deliveries
      // no further traversing necessary because deliveries can only have one pickup
      const relations = templateStop.pickupDeliveryPickup || [];
      for (let i = 0; i < relations.length; i++) {
        relatedTemplateStopIds.add(relations[i].deliveryStopId);
      }
    } else if (templateStop.stopType === TemplateStopType.Delivery) {
      // for a delivery stop get all related deliveries and their related pickups
      const relations = templateStop.pickupDeliveryDelivery || [];
      for (let i = 0; i < relations.length; i++) {
        const pickupStopId = relations[i].pickupStopId;
        relatedTemplateStopIds.add(pickupStopId);

        for (let j = 0; j < templateStops.length; j++) {
          const stop = templateStops[j];
          if (stop.id === pickupStopId) {
            const pickupRelations = stop.pickupDeliveryPickup || [];
            for (let k = 0; k < pickupRelations.length; k++) {
              relatedTemplateStopIds.add(pickupRelations[k].deliveryStopId);
            }
            break;
          }
        }
      }
    }

    const relatedTemplateStops = [];
    for (let i = 0; i < templateStops.length; i++) {
      if (relatedTemplateStopIds.has(templateStops[i].id)) {
        relatedTemplateStops.push(templateStops[i]);
      }
    }

    return relatedTemplateStops;
  }

  return (
    <div className={classes.root}>
      <Grid className={rootClassName} container spacing={1}>
        <Grid item lg={12} sm={12} xl={6} xs={12}>
          <div>
            <div className={classNames(classes.legend, classes.topSpacer)}>
              <TemplateStopIconLegend />
            </div>
            <BaseTemplateStopSelectionMap
              className={classes.map}
              displayControls
              templates={templates}
              unselectedUnassignedTemplateStops={
                unassignedTemplateStops
                  ? unassignedTemplateStops.filter(
                      (templateStop) =>
                        selectedUnassignedTemplateStops.findIndex(
                          (c) => c.id === templateStop.id
                        ) < 0
                    )
                  : []
              }
              selectedUnassignedTemplateStops={selectedUnassignedTemplateStops}
              selectedAssignedTemplateStops={selectedAssignedTemplateStops}
              onSelectUnassignedTemplateStops={selectUnassignedTemplateStops}
              onSelectAssignedTemplateStops={selectAssignedTemplateStops}
            />
          </div>
        </Grid>
        <Grid item lg={12} sm={12} xl={6} xs={12}>
          <div className={classes.tableSection}>
            <div className={classes.tableSettingsSection}>
              <InfoTooltip descriptionKey={'1'}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={templateStopConnectionSelection}
                      color="secondary"
                      onChange={(event) =>
                        setTemplateStopConnectionSelection(event.target.checked)
                      }
                    />
                  }
                  label={t('templateStopConnectionSelection')}
                />
              </InfoTooltip>
              <TableSettings
                buttonOptic
                className={classNames(classes.tableSettings, classes.topSpacer)}
                columns={Columns}
                tableConfig={config}
                updateConfig={updateConfig}
                useNamesAsTranslateString
              />
            </div>
            <TemplateStopSelectionTable
              className={classes.templateTable}
              displayMode={displayMode}
              templates={templates}
              unassignedTemplateStops={unassignedTemplateStops}
              selectedUnassignedTemplateStops={selectedUnassignedTemplateStops}
              selectedAssignedTemplateStops={selectedAssignedTemplateStops}
              onSelectAssignedTemplateStops={selectAssignedTemplateStops}
              onSelectUnassignedTemplateStops={selectUnassignedTemplateStops}
              columnConfigs={columnConfigs}
            />
            <div className={classes.buttonContainer}>
              <Button
                  className={classes.button}
                  color="primary"
                  onClick={() => {
                    setConfigureOptimizerAlgorithmDialogState({
                      ...configureOptimizerAlgorithmDialogState,
                      open: true,
                    });
                  }}
                  variant="contained"
              >
                <SettingsIcon />
                &nbsp;{t('ConfigureOptimizerAlgorithm')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={selectAllTemplateStops}
                className={classes.button}
                startIcon={<CheckCircleOutline />}
              >
                {t('selectAll')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={deselectAllTemplateStops}
                className={classes.button}
                startIcon={<RemoveCircleOutline />}
              >
                {t('deselectAll')}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>
      <ConfigureOptimizerAlgorithmDialog 
          onClose={onConfigureOptimizerAlgorithmCancel} 
          onSave={onConfigureOptimizerAlgorithmSave}
          configureOptimizerAlgorithmDialogState={configureOptimizerAlgorithmDialogState}
          config={optimizerAlgorithmConfiguration}
          stopsCount={templateStops.length}
      />
    </div>
  );
}

TemplateStopSelection.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  templates: PropTypes.array.isRequired,
  templateStops: PropTypes.array.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
  t: PropTypes.func.isRequired,
  selectedAssignedTemplateStops: PropTypes.array.isRequired,
  selectedUnassignedTemplateStops: PropTypes.array.isRequired,
  setSelectedAssignedTemplateStops: PropTypes.func.isRequired,
  setSelectedUnassignedTemplateStops: PropTypes.func.isRequired,
  configureOptimizerAlgorithmDialogState: PropTypes.object.isRequired,
  setConfigureOptimizerAlgorithmDialogState: PropTypes.func.isRequired,
  optimizerAlgorithmConfiguration: PropTypes.object.isRequired,
  onConfigureOptimizerAlgorithmCancel: PropTypes.func.isRequired,
  onConfigureOptimizerAlgorithmSave: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(TemplateStopSelection);
